const LOW_INFO_TITLES = new Set([
    'hey',
    'hi',
    'hello',
    'hiya',
    'yo',
    'sup',
    'hlo',
    'hii',
    'ok',
    'okay',
    'thanks',
    'thank you',
    'help',
    'i need help',
    'can you help',
    'help me',
    'new chat',
    'vehicle help',
    'car issue',
    'diagnostic report',
    'service request',
    // System-generated thread names with no meaningful user content
    'chat',
    'general',
    'untitled',
    'new',
    'create',
    'update',
    'start',
]);

// ---------------------------------------------------------------------------
// VIN / system-title detection
// ---------------------------------------------------------------------------

/**
 * A VIN is exactly 17 alphanumeric characters (no I, O, Q).
 * A partial VIN suffix (what the backend sometimes appends) is typically
 * 4–17 alphanumeric chars that mix digits and letters in a way no natural
 * English word does.
 *
 * Covers all observed backend auto-title patterns, now handled generically by
 * resolveAddVehicleTitle() (any arrangement, extra words, or truncated "…" form):
 *   "Add Vehicle {VIN}", "Add Your Vehicle {VIN}", "Vehicle Added {VIN}",
 *   "Add Vehicle {VIN} to my garage", "Add {VIN}" (bare form).
 */

/**
 * Also handle the short bare form: "add {VIN_fragment}" with no "vehicle" word.
 * Matched only when the trailing token is clearly alphanumeric (mixes letters
 * and digits) so we don't falsely catch real titles that start with "add".
 */
const ADD_BARE_VIN_RE = /^add\s+([A-Za-z0-9]{4,17})$/i;

/**
 * Plain variants with no VIN suffix.
 */
const ADD_VEHICLE_PLAIN_RE = /^(?:add(?:\s+(?:a|your|the|my))?\s+vehicle|vehicle\s+added)$/i;

/**
 * Returns true when a token looks like an opaque ID / VIN fragment rather
 * than a real English word. Heuristic: contains at least one digit AND at
 * least one letter, OR is fully numeric.
 */
function looksLikeVinFragment(token) {
    const hasDigit = /\d/.test(token);
    const hasLetter = /[A-Za-z]/.test(token);
    // Pure digit string (e.g. "12345") is also an ID/VIN fragment
    if (/^\d+$/.test(token)) return true;
    // Mixed alphanumeric = identifier
    return hasDigit && hasLetter;
}

/**
 * Backends truncate long auto-titles and append an ellipsis ("Add Vehicle 1J4FF58S...").
 * Strip that (and any stray trailing punctuation) so VIN detection isn't defeated by "...".
 */
function stripTrailingEllipsis(text) {
    return String(text || '').replace(/\s*(?:\.\s*){2,}$/, '').replace(/[\s.]+$/, '').trim();
}

/**
 * True when a single token is a VIN or VIN/ID fragment — as opposed to a legitimate short
 * model name (330i, F150, A4, Q5, CX5) or a DTC code (P0301, U0100), which we must KEEP.
 *
 * Rule: 17-char VIN, OR a 6+ char pure-numeric id, OR a 6+ char alphanumeric token that mixes
 * letters and digits. The 6-char floor is what separates VIN prefixes ("Wbaev5", "eg24sf",
 * "1j4ff58s") from model names (≤5 chars) and 5-char DTC codes.
 */
function isVinLikeToken(token) {
    const t = String(token || '').replace(/[^A-Za-z0-9]/g, '');
    if (t.length < 6) return false;
    if (/^[A-HJ-NPR-Z0-9]{17}$/i.test(t)) return true;      // full 17-char VIN
    if (/^\d{6,}$/.test(t)) return true;                     // long pure-numeric id
    return /[A-Za-z]/.test(t) && /\d/.test(t);               // 6+ char mixed alnum = VIN/ID
}

/**
 * Remove every VIN/ID-like token from a title so an opaque identifier can never leak into
 * the sidebar, regardless of where it sits in the string.
 */
function stripVinTokens(text) {
    return String(text || '')
        .split(/\s+/)
        .filter((tok) => !isVinLikeToken(tok))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * A full 17-char VIN: alphanumeric, no I/O/Q, exactly 17 chars.
 * This matches when the ENTIRE thread title is just the VIN the user typed.
 * e.g. "1J4FF58S4YL194693" or the lowercased form "1j4ff58s4yl194693"
 */
const STANDALONE_VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i;

/**
 * A partial / short VIN fragment (8-16 chars) used as the sole thread title.
 * e.g. "1j4ff58s" — clearly an ID, not a word.
 */
const PARTIAL_VIN_TITLE_RE = /^[A-Za-z0-9]{8,16}$/;

/**
 * Detect and translate system-generated "Add Vehicle {VIN}" titles, as well
 * as titles that ARE a raw VIN, into a human-readable label.
 *
 * Returns:
 *   - "Vehicle Added via VIN" when the title is a VIN or contains Add Vehicle + VIN
 *   - "Vehicle Added"         when the title is a plain "Add Vehicle" variant
 *   - null                    when the title is unrelated
 */
function resolveAddVehicleTitle(text) {
    const trimmed = stripTrailingEllipsis(String(text || '').trim());
    if (!trimmed) return null;

    // Whole title is a full 17-char VIN
    if (STANDALONE_VIN_RE.test(trimmed)) {
        return 'Vehicle Added via VIN';
    }

    // Whole title is a short alphanumeric fragment (partial VIN / ID) —
    // only if it mixes letters and digits (not a plain word)
    if (PARTIAL_VIN_TITLE_RE.test(trimmed) && looksLikeVinFragment(trimmed)) {
        return 'Vehicle Added via VIN';
    }

    // Any "add … vehicle …" / "vehicle added …" phrasing, in any arrangement and with extra
    // words ("Add Vehicle {VIN} to my garage", "Add Vehicle with VIN {VIN}", the truncated
    // "Add Vehicle {VIN}…"). Detect the add-vehicle intent, strip filler + VIN, and if nothing
    // meaningful remains, it is a pure add — label it cleanly with NO id.
    const isAddVehicle = (
        /\badd\b/i.test(trimmed) && /\bvehicle\b/i.test(trimmed)
    ) || /\bvehicle\s+added\b/i.test(trimmed);
    if (isAddVehicle) {
        const remainder = trimmed
            .split(/\s+/)
            .filter((t) => !isVinLikeToken(t))
            .join(' ')
            .replace(/\b(add|added|a|an|your|the|my|new|this|vehicle|with|vin|number|to|into|my|garage|car|please|pls)\b/gi, ' ')
            .replace(/[^A-Za-z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        const hasVinToken = trimmed.split(/\s+/).some(isVinLikeToken);
        // Nothing beyond "add vehicle" (+ VIN) → clean label. If there IS extra content
        // (e.g. "Add Vehicle Jeep Cherokee"), fall through to normal formatting (VIN stripped).
        if (!remainder) return hasVinToken ? 'Vehicle Added via VIN' : 'Vehicle Added';
    }

    // Bare "add {token}" — only treat as VIN-add when the token is ID-like
    const bareMatch = trimmed.match(ADD_BARE_VIN_RE);
    if (bareMatch && looksLikeVinFragment(bareMatch[1])) {
        return 'Vehicle Added via VIN';
    }

    if (ADD_VEHICLE_PLAIN_RE.test(trimmed)) {
        return 'Vehicle Added';
    }

    return null;
}

/**
 * "Decode {VIN}", "Look up VIN {VIN}", "VIN lookup {VIN}" → "Decode VIN".
 * Stripping the VIN from these would otherwise leave a bare, uninformative "Decode".
 */
function resolveVinActionTitle(text) {
    const trimmed = stripTrailingEllipsis(String(text || '').trim());
    if (!trimmed) return null;
    const hasVin = STANDALONE_VIN_RE.test(trimmed) || trimmed.split(/\s+/).some(isVinLikeToken);
    if (!hasVin) return null;
    if (/\b(decode|decoding|vin\s*look\s*up|look\s*up\s+(?:this\s+)?vin|lookup\s+vin)\b/i.test(trimmed)) {
        return 'Decode VIN';
    }
    return null;
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function titleCaseShort(text) {
    const smallWords = new Set(['and', 'or', 'for', 'to', 'of', 'in', 'on', 'at', 'with']);
    // Normalize curly/smart apostrophes to a straight ' first — otherwise a contraction
    // like "Won't" (with a smart ') isn't matched as one word and splits into "Won T".
    const words = String(text || '').replace(/[‘’ʼ]/g, "'").match(/[A-Za-z0-9]+(?:'[A-Za-z0-9]+)?/g) || [];
    return words
        .slice(0, 7)
        .map((word, index) => {
            const lower = word.toLowerCase();
            if (index > 0 && smallWords.has(lower)) return lower;
            if (['ac', 'obd', 'abs', 'tpms'].includes(lower)) return lower.toUpperCase();
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ')
        .trim();
}

function cleanTitle(title, fallback) {
    const compact = String(title || '')
        .replace(/[‘’ʼ]/g, "'")
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/^["'`*_#\-.!?\s]+|["'`*_#\-.!?\s]+$/g, '')
        .trim();

    if (!compact) return fallback;
    const shortened = compact.split(/\s+/).slice(0, 7).join(' ');
    return shortened.length > 60 ? `${shortened.slice(0, 57)}...` : shortened;
}

// These tokens are low-info ONLY as a WHOLE bare title — they legitimately BEGIN real
// titles ("Update Odometer Reading", "New Battery Issue", "Start Won't Crank"), so they
// must NOT be rejected just for being the first word (which turned every such title into
// "New Chat" in the sidebar).
const WHOLE_TITLE_ONLY_LOW_INFO = new Set(['new', 'update', 'create', 'start', 'chat', 'general', 'untitled']);

export function isLowInfoTitle(title = '') {
    const normalized = String(title).trim().toLowerCase().replace(/[!.,?]+$/g, '');
    if (!normalized) return true;
    const words = normalized.split(/\s+/);
    if (words.length > 4) return false;
    if (LOW_INFO_TITLES.has(normalized)) return true;
    // First-word match only for true greeting/filler words — never for the action verbs
    // above, so a real title like "Update Odometer Reading" is kept.
    return LOW_INFO_TITLES.has(words[0]) && !WHOLE_TITLE_ONLY_LOW_INFO.has(words[0]);
}

function extractTitleSubject(text = '') {
    const cleaned = String(text || '')
        .replace(/\b(won'?t|wont)\b/gi, 'will not')
        .replace(/\b(can'?t|cant)\b/gi, 'cannot')
        .replace(/\b(doesn'?t|doesnt)\b/gi, 'does not')
        .replace(/\b(my|the|a|an|car|vehicle|auto|please|pls|can you|could you|i need|need help|help|diagnose|diagnosing|problem|issue|question|with|about)\b/gi, ' ')
        .replace(/[^A-Za-z0-9\s'/-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return titleCaseShort(cleaned);
}

// Connectors/fillers that must never END a title (a truncated symptom that stops on one —
// e.g. "…From Rear When" — reads as meaningless, so trim them off the tail).
const TRAILING_STOP_RE = /(?:\s+(?:when|and|or|but|the|a|an|of|for|to|with|on|at|in|is|are|was|were|my|your|that|this|from|into|as|if|so|than|then|while|because|it|its|also|just|still|after|before|during|over|under))+\s*$/i;

export function trimTrailingStopwords(text = '') {
    let out = String(text || '').trim();
    let prev;
    do { prev = out; out = out.replace(TRAILING_STOP_RE, '').trim(); } while (out !== prev);
    return out || String(text || '').trim();
}

export function formatSidebarTitle(rawTitle = '', fallback = 'Vehicle Diagnosis') {
    let text = stripTrailingEllipsis(String(rawTitle || '').replace(/[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim());
    // An opaque VIN/ID must never leak into a sidebar title.
    const strippedVin = stripVinTokens(text);
    if (strippedVin && strippedVin !== text) text = strippedVin;
    // Drop a trailing "Diagnosis"/"Diagnostic" — users want the plain symptom name
    // ("Car Won't Start", not "Car Won't Start Diagnosis").
    text = text.replace(/\s+diagnos(is|tic)\b\.?$/i, '').trim();
    // Drop a dangling connector left by truncation ("…Rear When" → "…Rear").
    text = trimTrailingStopwords(text);
    if (!text || isLowInfoTitle(text)) return fallback;

    const lower = text.toLowerCase();
    const subject = extractTitleSubject(text);

    if (/\b(find|buy|order|lookup|options|search|part|parts)\b/.test(lower) && subject) {
        const title = /\b(Options|Lookup|Search)\b/i.test(subject) ? subject : `${subject} Options`;
        return cleanTitle(title, 'Parts Lookup');
    }
    if (/\b(shop|mechanic|book|appointment|estimate|quote|send report)\b/.test(lower) && subject) {
        const title = /\b(Book|Shop|Appointment|Estimate|Quote)\b/i.test(subject) ? subject : `Find Shop for ${subject}`;
        return cleanTitle(title, 'Find Repair Shop');
    }
    if (/\b(lift|winch|upgrade|modify|modification|plan)\b/.test(lower) && subject) {
        const title = /\b(Plan|Upgrade)\b/i.test(subject) ? subject : `${subject} Plan`;
        return cleanTitle(title, 'Modification Planning');
    }
    if (/\b(log|logged|replaced|changed|installed|completed|done)\b/.test(lower) && subject) {
        const title = /\b(Logged|Changed|Installed|Completed|Done|Replaced)\b/i.test(subject) ? subject : `${subject} Logged`;
        return cleanTitle(title, 'Service Logged');
    }
    // Diagnostic symptom → show the issue name as-is, with NO "Diagnosis" suffix.
    return cleanTitle(text, fallback);
}

export function formatChatThreadTitle(rawTitle = '', fallback = 'New Chat') {
    let text = stripTrailingEllipsis(
        String(rawTitle || '').replace(/[\r\n\t]+/g, ' ').replace(/\s+/g, ' ').trim()
    );

    // Handle "Add Vehicle {VIN}" or plain "Add Vehicle" system-generated titles
    // before any other processing so they display cleanly.
    const addVehicleLabel = resolveAddVehicleTitle(text);
    if (addVehicleLabel) return addVehicleLabel;

    // "Decode {VIN}" / "Look up VIN {VIN}" → "Decode VIN" (stripping the VIN alone would
    // leave a bare "Decode").
    const vinActionLabel = resolveVinActionTitle(text);
    if (vinActionLabel) return vinActionLabel;

    // Belt-and-suspenders: strip any stray VIN/ID token from an otherwise normal title so an
    // opaque id never leaks into the sidebar (e.g. "Diagnose 1J4FF58S… Won't Start").
    const stripped = stripVinTokens(text);
    if (stripped && stripped !== text) text = stripped;

    if (!text || isLowInfoTitle(text)) return fallback;
    return cleanTitle(titleCaseShort(text), fallback);
}
